import React from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import {
  EngagementChosenAnswer,
  TrainingCenterBundleItemVideoEngagementQuestion,
} from "../../system/AppState/AppStateTrainingCenter";
import { clickySound } from "../../utils/Sounds";
import { TC_TIMES_SVG } from "../../utils/tc_svgs";
import { AppIcon } from "../Generic/AppIcon";
import {
  lookupTranslationRequired,
  TrainingCenterLanguagePreference,
} from "./TrainingCenterLanguageLookup";

interface Props {
  currentSecond: number;
  engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion;
  chosenAnswer: EngagementChosenAnswer | undefined;
  timeLeft: number;
  languagePreference: TrainingCenterLanguagePreference;
  onClickAnswer: (
    engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion,
    answerValue: string,
    currentSecond: number
  ) => void;
  onClickClose: (engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion) => void;
  onBeforeTimeoutEnd: () => void;
}

const TrainingCenterVideoEngagementQuestion = ({
  currentSecond,
  engagementQuestion,
  chosenAnswer,
  timeLeft,
  languagePreference,
  onClickAnswer,
  onClickClose,
  onBeforeTimeoutEnd: onTimeoutAboutToEnd,
}: Props) => {
  React.useEffect(() => {
    clickySound.play();
  }, []);

  React.useEffect(() => {
    if (timeLeft === 1 && chosenAnswer === undefined) {
      onTimeoutAboutToEnd();
    }
  }, [timeLeft, chosenAnswer, onTimeoutAboutToEnd]);

  const handleClickAnswer = React.useCallback(
    ($event: React.MouseEvent<HTMLElement>, answer: string) => {
      $event.stopPropagation();
      onClickAnswer(engagementQuestion, answer, currentSecond);
    },
    [onClickAnswer, engagementQuestion, currentSecond]
  );

  const handleClickClose = React.useCallback(
    ($event: React.MouseEvent<HTMLElement>) => {
      $event.stopPropagation();
      onClickClose(engagementQuestion);
    },
    [onClickClose, engagementQuestion]
  );

  const { questionText, questionAnswers } = React.useMemo(
    () => lookupTranslationRequired(languagePreference, engagementQuestion.displayData, (x) => x),
    [languagePreference, engagementQuestion.displayData]
  );

  const answers = React.useMemo(() => {
    return [...questionAnswers]
      .filter((answer) => answer !== "")
      .sort((a, b) => a.localeCompare(b));
  }, [questionAnswers]);

  return (
    <div className="TrainingCenterVideoEngagementQuestion">
      <div className="_question">
        <AppIcon icon={TC_TIMES_SVG} className="close" onClick={handleClickClose} />
        <span>{questionText}</span>
      </div>
      <div className="_answers_countdown">
        <div className="_answers">
          {answers.map((answer, i) => (
            <div
              key={i}
              className="_answer"
              onClick={($event) => handleClickAnswer($event, answer)}
            >
              {answer}
            </div>
          ))}
        </div>
        <div className="_countdown">
          <CircularProgressbar
            value={timeLeft}
            maxValue={engagementQuestion.duration}
            strokeWidth={10}
            text={timeLeft.toString()}
            background={true}
            counterClockwise={true}
            styles={circularProgressbarStyles}
          />
        </div>
      </div>
    </div>
  );
};

const circularProgressbarStyles = {
  background: {
    fill: "#FFF",
  },
  trail: {
    stroke: "#89CCF8",
  },
  path: {
    stroke: "#0A77E6",
  },
  text: {
    fill: "#272860",
    fontWeight: 600,
    fontFamily: "Nunito Sans",
    fontSize: 32,
  },
};

export default TrainingCenterVideoEngagementQuestion;
